import dayjs from "dayjs"

const MILESTONE_KEYS = [
  "activity_due_date",
  "actual_completion_date",
  "compliance_due_date",
  "fcsr",
  "fpi",
  "fsa",
  "lpo",
  "pcd",
  "pra",
  "projected_completion_date",
  "request_due_date",
]

const getIsWorkflowComplete = (workflowTrackerData) => {
  const td = workflowTrackerData.state_data.tracker_data
  return td?.[td?.length - 1]?.is_current ? true : false
}

const getMilestones = (workflowTrackerData) => {
  let ret = []
  MILESTONE_KEYS.forEach((key) => {
    if (workflowTrackerData.parent_data?.[key]) {
      ret.push({
        type: key,
        value: workflowTrackerData.parent_data[key],
      })
    }
  })

  return ret
}

export const formatTrackerData = (workflowTrackerData) => {
  if (!workflowTrackerData?.state_data) return []
  const milestones = getMilestones(workflowTrackerData)
  const isWorkflowComplete = getIsWorkflowComplete(workflowTrackerData)

  let trackerData = workflowTrackerData?.state_data?.tracker_data?.slice(1)

  const today = dayjs()
  let foundToday = false

  if (trackerData.length > 0 && milestones) {
    const tempMilestones = milestones
      .filter((milestone) => {
        if (isWorkflowComplete && milestone.type === "projected_completion_date") return false
        return true
      })
      .map((milestone) => ({
        ...milestone,
        eligible: true,
      }))

    for (let i = trackerData.length - 1; i >= 0; i--) {
      let state = trackerData[i]
      let nextState = trackerData[i + 1]
      const isLast = i === trackerData.length - 1
      const isFirst = i === 0
      let isToday = false

      if (state.is_current && !state?.end_state_date) {
        state.end_state_date = nextState?.start_state_date
      }

      if (foundToday === false) {
        if (state?.start_state_date && today.isSame(state.start_state_date, "day")) {
          isToday = true
        } else if (
          trackerData?.[i + 1]?.start_state_date !== undefined &&
          today.isSame(trackerData?.[i + 1]?.start_state_date, "day")
        ) {
          isToday = true
        } else if (
          state?.start_state_date &&
          trackerData?.[i + 1]?.start_state_date &&
          today.isBetween(state.start_state_date, trackerData[i + 1].start_state_date)
        ) {
          isToday = true
        }

        if (isToday) {
          foundToday = true
        }
      }

      const stateMilestones = []
      tempMilestones.forEach((milestone) => {
        let milestoneDate = milestone.value

        if (milestone.eligible) {
          if (state.start_state_date && state.end_state_date) {
            let startDate = state.start_state_date
            let endDate = state.end_state_date

            if (
              dayjs(milestoneDate).isBetween(startDate, endDate) ||
              milestoneDate === startDate ||
              milestoneDate === endDate ||
              (isLast && dayjs(milestoneDate).isAfter(startDate)) ||
              (isFirst && dayjs(milestoneDate).isBefore(startDate))
            ) {
              stateMilestones.push(milestone)
              milestone.eligible = false
            }
          } else if (isFirst && dayjs(milestoneDate).isSameOrBefore(state.start_state_date)) {
            stateMilestones.push(milestone)
            milestone.eligible = false
          } else if (isLast && dayjs(milestone.value).isSameOrAfter(state.start_state_date)) {
            stateMilestones.push(milestone)
            milestone.eligible = false
          }
        }
      })

      state.milestones = stateMilestones
      state.isToday = isToday
    }
  }

  return trackerData
}

export const handleTrackerScroll = (event, wrapRef1, wrapRef2) => {
  const targetDiv = event.target
  var scrolling = false

  if (targetDiv === wrapRef1.current && wrapRef2.current) {
    if (scrolling) {
      scrolling = false
      return true
    }
    scrolling = true

    wrapRef2.current.scrollLeft = targetDiv.scrollLeft
  } else if (targetDiv === wrapRef2.current && wrapRef1.current) {
    if (scrolling) {
      scrolling = false
      return true
    }
    scrolling = true
    wrapRef1.current.scrollLeft = targetDiv.scrollLeft
  }
}

export const formatPutRequest = (stateData, model, workflowTrackerData) => {
  let assessmentPk = null

  let projectedCompletionDate = stateData[stateData.length - 1].end_state_date
  const lastState = stateData[stateData.length - 1]
  if (!lastState.end_state_date) {
    projectedCompletionDate = dayjs(lastState.start_state_date).add(lastState.goal_days, "day").format("YYYY-MM-DD")
  }

  let actionData = []

  stateData.forEach((state, i) => {
    let currState = { ...state }
    let startDate = state?.start_state_date

    if (i !== 0 && i !== stateData.length - 1 && stateData?.[i - 1].end_state_date) {
      if (!dayjs(startDate).isSame(stateData?.[i - 1].end_state_date, "day")) {
        startDate = stateData?.[i - 1].end_state_date
      }
    }

    let endDate = stateData?.[i + 1]?.start_state_date
    let goalDays = dayjs(endDate).diff(dayjs(startDate), "day")

    if (dayjs(state.start_state_date).diff(state.end_state_date, "day") < 0) {
      currState.end_state_date = state.start_state_date
    }

    if (goalDays < 0) {
      goalDays = 0
      endDate = startDate
      let prevState = actionData?.[i - 1]

      if (prevState) {
        prevState.end_state_date = startDate
        prevState.goal_days = dayjs(startDate).diff(dayjs(prevState.start_state_date), "day")
      }
    }

    const ret = {
      ...currState,
      is_first_state: i === 0,
      is_last_state: i === stateData.length - 1,
      assign_to_ids: currState.assigned_to_ids || [],
      start_state_date: dayjs(startDate).format("YYYY-MM-DD"),
      end_state_date: dayjs(endDate).format("YYYY-MM-DD"),
      goal_days: goalDays || 0,
    }
    actionData.push(ret)
  })

  let req = {
    model,
    projected_completion_date: projectedCompletionDate,
    action_data: actionData,
  }

  if (assessmentPk) req.assessment_pk = assessmentPk
  if (workflowTrackerData?.parent_data?.actual_completion_date)
    req.actual_completion_date = workflowTrackerData.parent_data.actual_completion_date
  if (workflowTrackerData.parent_data.activity_due_date)
    req.activity_due_date = workflowTrackerData.parent_data.activity_due_date

  return req
}
